import { MetodoPagoSINField } from '../common/HomologacionSINFields';
import React, { useState, useEffect } from 'react';
import { createTipoPago } from '../../services/tiposPagoService';
import { fetchConfiguracionesPago, ConfiguracionPago } from '../../services/configuracionPagoService';

interface AddTiposPagosProps {
    isOpen: boolean;
    onClose: () => void;
    onTipoPagoCreated: () => void;
}

const AddTiposPagos: React.FC<AddTiposPagosProps> = ({ isOpen, onClose, onTipoPagoCreated }) => {
    const [metodoSIN, setMetodoSIN] = useState<number | null>(null);
    const [nombre, setNombre] = useState('');
    const [descripcion, setDescripcion] = useState('');
    const [activo, setActivo] = useState(true);
    const [configuracionDePago, setConfiguracionDePago] = useState<number | ''>('');
    const [configuraciones, setConfiguraciones] = useState<ConfiguracionPago[]>([]);
    const [error, setError] = useState<string | null>(null);
    const [isSubmitting, setIsSubmitting] = useState(false);

    // Cargar configuraciones al abrir el modal
    useEffect(() => {
        if (isOpen) {
            const loadConfiguraciones = async () => {
                try {
                    const data = await fetchConfiguracionesPago();
                    // Filtrar solo activas si es necesario
                    const configuracionesActivas = data.filter(config => config.activo);
                    setConfiguraciones(configuracionesActivas);
                } catch (err) {
                    console.error(err);
                    setError('No se pudieron cargar las configuraciones de pago');
                }
            };
            loadConfiguraciones();
        }
    }, [isOpen]);

    const handleSubmit = async (e: React.FormEvent) => {
        e.preventDefault();
        setError(null);
        setIsSubmitting(true);

        try {
            if (!nombre.trim()) {
                throw new Error('El nombre es obligatorio');
            }
            if (!configuracionDePago) {
                throw new Error('Debe seleccionar una configuración de pago');
            }

            await createTipoPago({
                nombre: nombre.trim(),
                descripcion: descripcion.trim(),
                activo,
                configuracion_de_pago: Number(configuracionDePago), codigo_metodo_pago_sin: metodoSIN
            });

            // Resetear el formulario
            setNombre('');
            setDescripcion('');
            setActivo(true);
            setConfiguracionDePago('');
            
            // Cerrar modal y actualizar lista
            onTipoPagoCreated();
            onClose();
        } catch (err) {
            setError(err instanceof Error ? err.message : 'Error al crear el tipo de pago');
        } finally {
            setIsSubmitting(false);
        }
    };

    if (!isOpen) return null;

    return (
        <div className="fixed inset-0 z-50 flex items-center justify-center">
            <div className="fixed inset-0 bg-black bg-opacity-50 backdrop-blur-sm" />
            <div className="bg-white p-6 rounded-xl w-full max-w-md mx-4 border border-gray-100 shadow-xl relative">
                <div className="flex justify-between items-start mb-4">
                    <h3 className="text-xl font-bold text-gray-900">Agregar Tipo de Pago</h3>
                    <div className="flex items-center gap-4">
                        {error && <div className="text-red-500 text-sm">{error}</div>}
                        <button 
                            onClick={onClose} 
                            className="text-gray-400 hover:text-gray-600 transition-colors"
                        >
                            <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                            </svg>
                        </button>
                    </div>
                </div>

                <form onSubmit={handleSubmit}>
                    <MetodoPagoSINField codigo={metodoSIN} onChange={setMetodoSIN} />
                    <div className="mb-4">
                        <label className="block text-sm font-medium text-gray-700 mb-2">
                            Nombre *
                        </label>
                        <input
                            type="text"
                            required
                            className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                            value={nombre}
                            onChange={(e) => setNombre(e.target.value)}
                            placeholder="Ej: Transferencia Bancaria"
                            disabled={isSubmitting}
                        />
                    </div>
                    
                    <div className="mb-4">
                        <label className="block text-sm font-medium text-gray-700 mb-2">
                            Descripción
                        </label>
                        <textarea
                            className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 h-32"
                            value={descripcion}
                            onChange={(e) => setDescripcion(e.target.value)}
                            placeholder="Descripción opcional"
                            disabled={isSubmitting}
                        />
                    </div>

                    <div className="mb-4">
                        <label className="block text-sm font-medium text-gray-700 mb-2">
                            Configuración de Pago *
                        </label>
                        <select
                            required
                            className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                            value={configuracionDePago}
                            onChange={(e) => setConfiguracionDePago(e.target.value ? Number(e.target.value) : '')}
                            disabled={isSubmitting}
                        >
                            <option value="">Seleccione una configuración</option>
                            {configuraciones.map((config) => (
                                <option key={config.id} value={config.id}>
                                    {config.nombre} ({config.tipo})
                                </option>
                            ))}
                        </select>
                        <p className="text-xs text-gray-500 mt-1">
                            Define el comportamiento de este tipo de pago
                        </p>
                    </div>

                    <div className="mb-6">
                        <div className="flex items-center justify-between bg-gray-50 p-3 rounded-lg">
                            <div>
                                <span className="text-sm font-medium text-gray-700">
                                    Estado del Tipo de Pago
                                </span>
                                <p className="text-xs text-gray-500 mt-1">
                                    {activo ? 'Activo' : 'Inactivo'}
                                </p>
                            </div>
                            <label className="relative inline-flex items-center cursor-pointer">
                                <input
                                    type="checkbox"
                                    className="sr-only"
                                    checked={activo}
                                    onChange={() => setActivo(!activo)}
                                    disabled={isSubmitting}
                                />
                                <div
                                    className={`w-10 h-6 rounded-full transition-colors ${activo ? 'bg-[#c1348b]' : 'bg-gray-400'}`}
                                >
                                    <div
                                        className={`absolute top-1 left-1 bg-white w-4 h-4 rounded-full shadow-sm transform transition-transform ${activo ? 'translate-x-5' : 'translate-x-0'}`}
                                    ></div>
                                </div>
                            </label>
                        </div>
                    </div>

                    <div className="flex justify-end gap-3 mt-6">
                        <button
                            type="button"
                            onClick={onClose}
                            className="px-4 py-2 text-sm text-gray-700 bg-gray-100 hover:bg-gray-200 rounded-lg transition-colors font-medium"
                            disabled={isSubmitting}
                        >
                            Cancelar
                        </button>
                        <button
                            type="submit"
                            disabled={isSubmitting}
                            className="px-4 py-2 text-sm bg-[#c1348b] hover:bg-[#8f1f64] text-white rounded-lg transition-colors font-medium flex items-center"
                        >
                            {isSubmitting ? (
                                <span className="flex items-center">
                                    <svg className="animate-spin h-5 w-5 mr-2" viewBox="0 0 24 24">
                                        <circle
                                            className="opacity-25"
                                            cx="12"
                                            cy="12"
                                            r="10"
                                            stroke="currentColor"
                                            strokeWidth="4"
                                            fill="none"
                                        />
                                        <path
                                            className="opacity-75"
                                            fill="currentColor"
                                            d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                                        />
                                    </svg>
                                    Creando...
                                </span>
                            ) : (
                                'Crear Tipo de Pago'
                            )}
                        </button>
                    </div>
                </form>
            </div>
        </div>
    );
};

export default AddTiposPagos;
